import { useState, useEffect } from "react";
import "@/App.css";
import { Menu, X, Play, Send, CheckCircle, AlertCircle, Mail, Clock, User, ShoppingBag, BookOpen, Palette, Package, Sparkles, ChevronRight } from "lucide-react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ETSY_URL = "https://urbanadventuregifts.etsy.com";

// Navigation Component
const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const scrollToSection = (hash) => {
    setMobileOpen(false);
    if (location.pathname !== '/') return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="main-nav" data-testid="main-nav">
      <Link to="/" className="nav-logo" data-testid="nav-logo">
        INTONA <span>👑</span>
      </Link>
      <button 
        className="mobile-menu-btn" 
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      <ul className={`nav-links ${mobileOpen ? 'mobile-open' : ''}`} data-testid="nav-links">
        <li><Link to="/" onClick={() => setMobileOpen(false)}>Home</Link></li>
        <li><a href="/#storywear" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); scrollToSection('#storywear'); }}}>Storywear</a></li>
        <li><a href="/#christmas" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); scrollToSection('#christmas'); }}}>Christmas Drop</a></li>
        <li><Link to="/archive" onClick={() => setMobileOpen(false)}>Archive</Link></li>
        <li><a href="/#contact" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); scrollToSection('#contact'); }}}>Contact</a></li>
      </ul>
    </nav>
  );
};

// Hero Section (NEW Storywear focus)
const Hero = ({ onOpenOrderForm }) => (
  <section className="hero-new" id="home" data-testid="hero-section">
    <div className="hero-pattern"></div>
    <div className="hero-content">
      <div className="hero-crown-small">👑</div>
      <p className="hero-eyebrow">INTONA</p>
      <h1 className="hero-main-title">Stories for Little Crowns</h1>
      <p className="hero-subheadline">Now introducing <em>Little Readers Storywear</em></p>
      <p className="hero-body">
        Personalised pyjama tops inspired by the stories your family reads together.
      </p>
      <div className="hero-tags">
        <span className="hero-tag">Caribbean Heritage</span>
        <span className="hero-tag">Natural Hair Pride</span>
        <span className="hero-tag">Potwa & English</span>
        <span className="hero-tag">Ages 0–8</span>
      </div>
      <div className="hero-cta">
        <button onClick={onOpenOrderForm} className="btn-primary" data-testid="view-storywear-btn">
          Start Your Order <ChevronRight size={16} />
        </button>
        <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="etsy-hero-btn">
          <ShoppingBag size={16} /> Order on Etsy
        </a>
      </div>
    </div>
  </section>
);

// Storywear Intro Section
const StorywearIntro = ({ onOpenOrderForm }) => (
  <section className="storywear-intro" id="storywear" data-testid="storywear-intro">
    <div className="section-container">
      <p className="section-eyebrow">The New Offer</p>
      <h2 className="section-heading">Little Readers Storywear</h2>
      <div className="storywear-grid">
        <div className="storywear-text">
          <p className="lead-text">
            Most children's story clothing starts with a <em>character</em>.
          </p>
          <p className="lead-text accent">
            Storywear starts with your family's <em>reading routine</em>.
          </p>
          <p className="body-text">
            Tell us the book and theme your child is enjoying this month. 
            We create original artwork for a cosy pyjama top, inspired by that 
            story world and made for your family's own memories.
          </p>
          <button onClick={onOpenOrderForm} className="btn-primary" data-testid="start-order-btn" style={{ marginTop: '1rem' }}>
            Start Your Order <ChevronRight size={16} />
          </button>
        </div>
        <div className="storywear-visual">
          <div className="pyjama-mockup">
            <div className="pyjama-top">
              <div className="pyjama-neck"></div>
              <div className="pyjama-sleeves">
                <div className="pyjama-sleeve left"></div>
                <div className="pyjama-sleeve right"></div>
              </div>
              <div className="pyjama-design">
                <div className="design-element design-star">✨</div>
                <div className="design-text">Your Story</div>
                <div className="design-element design-moon">🌙</div>
              </div>
            </div>
            <p className="pyjama-tag">Original artwork · Made to order</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// How It Works Section
const stepsData = [
  { icon: BookOpen, title: "Choose the story", desc: "Tell us the book title, author and broad story theme." },
  { icon: Package, title: "Choose the size", desc: "Select the pyjama top size for your child." },
  { icon: Palette, title: "Share preferences", desc: "Choose colours, animals, objects and optional broad family representation." },
  { icon: Sparkles, title: "We create the artwork", desc: "Original storywear artwork is created in our studio." },
  { icon: CheckCircle, title: "Approve and receive", desc: "You get one preview and one small revision before printing." }
];

const HowItWorks = () => (
  <section className="how-it-works" id="how-it-works" data-testid="how-it-works">
    <div className="section-container">
      <p className="section-eyebrow">The Process</p>
      <h2 className="section-heading">How It Works</h2>
      <div className="steps-grid">
        {stepsData.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} className="step-card" data-testid={`step-${i + 1}`}>
              <div className="step-num">0{i + 1}</div>
              <div className="step-icon"><Icon size={28} /></div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Storywear Promise
const promiseData = [
  "No child names printed.",
  "No child photos required.",
  "No copied book characters.",
  "No copied book covers or illustrations.",
  "Original artwork only.",
  "Made to order."
];

const StorywearPromise = () => (
  <section className="promise-section" data-testid="promise-section">
    <div className="section-container">
      <p className="section-eyebrow light">Trust</p>
      <h2 className="section-heading light">Our Storywear Promise</h2>
      <div className="promise-grid">
        {promiseData.map((item, i) => (
          <div key={i} className="promise-item" data-testid={`promise-${i}`}>
            <CheckCircle size={20} className="promise-check" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Christmas Gallery / Lookbook
const christmasDesigns = [
  { emoji: "⭐", title: "Starry Night Reading", theme: "Classic bedtime stories", color: "#8B1A1A" },
  { emoji: "❄️", title: "Winter Wonderland", theme: "Snow-day stories", color: "#1a4d6b" },
  { emoji: "🦌", title: "Forest Adventures", theme: "Woodland tales", color: "#2F6B2F" },
  { emoji: "🎄", title: "Christmas Eve Classics", theme: "Holiday tradition", color: "#6B1A1A" },
  { emoji: "🌙", title: "Moonlight Chapters", theme: "Nighttime adventures", color: "#2A2456" },
  { emoji: "🎁", title: "The Gift of Stories", theme: "Custom family themes", color: "#8B4A1A" }
];

const ChristmasGallery = () => (
  <section className="christmas-gallery" data-testid="christmas-gallery">
    <div className="section-container">
      <p className="section-eyebrow">The Lookbook</p>
      <h2 className="section-heading">Christmas Design Inspiration</h2>
      <p className="gallery-intro">
        Each design is created from scratch, inspired by the stories your family loves. 
        Here's a preview of the magical worlds we love to bring to life.
      </p>
      <div className="gallery-grid">
        {christmasDesigns.map((design, i) => (
          <div 
            key={i} 
            className="gallery-item" 
            data-testid={`gallery-item-${i}`}
            style={{ '--card-color': design.color }}
          >
            <div className="gallery-visual">
              <div className="gallery-emoji">{design.emoji}</div>
              <div className="gallery-overlay">
                <p className="gallery-overlay-label">Preview Concept</p>
              </div>
            </div>
            <div className="gallery-info">
              <h3 className="gallery-title">{design.title}</h3>
              <p className="gallery-theme">{design.theme}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="gallery-note">
        <Sparkles size={14} /> All artwork is 100% original — made just for your family
      </p>
    </div>
  </section>
);

// Order Form Modal
const OrderFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    book_title: '',
    book_author: '',
    story_theme: '',
    child_age: '',
    pyjama_size: '',
    preferences: ''
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${API}/orders`, formData);
      setStatus({ 
        type: 'success', 
        message: 'Thank you! Your Storywear inquiry has been received. We\'ll be in touch within 48 hours.' 
      });
      setFormData({ parent_name: '', email: '', book_title: '', book_author: '', story_theme: '', child_age: '', pyjama_size: '', preferences: '' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="order-modal-overlay" onClick={onClose} data-testid="order-modal">
      <div className="order-modal" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal-close" onClick={onClose} data-testid="order-modal-close">
          <X size={24} />
        </button>
        <div className="order-modal-header">
          <p className="section-eyebrow">Storywear Inquiry</p>
          <h2 className="order-modal-title">Tell us about your little reader</h2>
          <p className="order-modal-subtitle">We'll create original artwork inspired by their favourite story</p>
        </div>
        <form className="order-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Your Name</label>
              <input type="text" name="parent_name" value={formData.parent_name} onChange={handleChange} required placeholder="Parent name" data-testid="order-parent-name" />
            </div>
            <div className="form-group">
              <label>Your Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" data-testid="order-email" />
            </div>
          </div>

          <div className="form-section-divider">
            <span>About the Story</span>
          </div>

          <div className="form-group">
            <label>Book Title *</label>
            <input type="text" name="book_title" value={formData.book_title} onChange={handleChange} required placeholder="e.g., The Snowy Day" data-testid="order-book-title" />
          </div>

          <div className="form-group">
            <label>Author (optional)</label>
            <input type="text" name="book_author" value={formData.book_author} onChange={handleChange} placeholder="e.g., Ezra Jack Keats" data-testid="order-book-author" />
          </div>

          <div className="form-group">
            <label>Story Theme *</label>
            <textarea name="story_theme" value={formData.story_theme} onChange={handleChange} required rows="2" placeholder="e.g., Winter adventures, friendship, imagination..." data-testid="order-theme" />
          </div>

          <div className="form-section-divider">
            <span>About Your Child</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Child's Age *</label>
              <select name="child_age" value={formData.child_age} onChange={handleChange} required data-testid="order-age">
                <option value="">Select age</option>
                <option value="0-1">0–1 years</option>
                <option value="1-2">1–2 years</option>
                <option value="2-3">2–3 years</option>
                <option value="3-4">3–4 years</option>
                <option value="4-5">4–5 years</option>
                <option value="5-6">5–6 years</option>
                <option value="6-7">6–7 years</option>
                <option value="7-8">7–8 years</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pyjama Size *</label>
              <select name="pyjama_size" value={formData.pyjama_size} onChange={handleChange} required data-testid="order-size">
                <option value="">Select size</option>
                <option value="0-6 months">0–6 months</option>
                <option value="6-12 months">6–12 months</option>
                <option value="1-2 years">1–2 years</option>
                <option value="2-3 years">2–3 years</option>
                <option value="3-4 years">3–4 years</option>
                <option value="4-5 years">4–5 years</option>
                <option value="5-6 years">5–6 years</option>
                <option value="6-7 years">6–7 years</option>
                <option value="7-8 years">7–8 years</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Design Preferences (optional)</label>
            <textarea name="preferences" value={formData.preferences} onChange={handleChange} rows="3" placeholder="Favourite colours, animals, objects, or family representation preferences..." data-testid="order-preferences" />
          </div>

          <div className="order-form-reminder">
            <CheckCircle size={16} /> No child names or photos needed. Original artwork only.
          </div>

          <button type="submit" className="btn-primary full-width" disabled={isSubmitting} data-testid="order-submit-btn">
            {isSubmitting ? 'Sending...' : <><Send size={16} /> Submit Inquiry</>}
          </button>
          {status.type && (
            <div className={`form-status ${status.type}`} data-testid="order-status">
              {status.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{status.message}</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

// Christmas Drop
const ChristmasDrop = ({ onOpenOrderForm }) => (
  <section className="christmas-drop" id="christmas" data-testid="christmas-drop">
    <div className="section-container">
      <div className="christmas-content">
        <div className="christmas-ornament">❄️</div>
        <p className="section-eyebrow light">Coming November</p>
        <h2 className="section-heading light">Christmas Storywear Drop</h2>
        <p className="christmas-body">
          Personalised pyjama tops for Christmas Eve boxes, bedtime stories, 
          family photos and little readers who love magical winter books.
        </p>
        <div className="hero-cta center">
          <button onClick={onOpenOrderForm} className="btn-primary" data-testid="join-christmas-btn">
            Start Your Order <ChevronRight size={16} />
          </button>
          <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-outline-light" data-testid="christmas-etsy-btn">
            <ShoppingBag size={16} /> Order on Etsy
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Contact Form Section
const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ 
        type: 'success', 
        message: 'Thank you! Your message has been sent. We\'ll get back to you soon.' 
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Something went wrong. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-new" id="contact" data-testid="contact-section">
      <div className="section-container">
        <div className="contact-grid">
          <div className="contact-info-new">
            <p className="section-eyebrow">Get in Touch</p>
            <h2 className="section-heading">Let's Create Something Special</h2>
            <p className="body-text">
              Questions about Storywear? Want to collaborate? 
              We'd love to hear from your family.
            </p>
            <div className="contact-detail-item">
              <Mail size={18} />
              <a href="mailto:theresahatchett5@gmail.com">theresahatchett5@gmail.com</a>
            </div>
            <div className="contact-detail-item">
              <ShoppingBag size={18} />
              <a href={ETSY_URL} target="_blank" rel="noopener noreferrer">Visit our Etsy shop</a>
            </div>
          </div>
          <form className="contact-form-new" onSubmit={handleSubmit} data-testid="contact-form">
            <div className="form-group">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
                data-testid="contact-name-input"
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Your email"
                data-testid="contact-email-input"
              />
            </div>
            <div className="form-group">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="4"
                placeholder="Your message..."
                data-testid="contact-message-input"
              />
            </div>
            <button 
              type="submit" 
              className="btn-primary full-width"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? 'Sending...' : <><Send size={16} /> Send Message</>}
            </button>
            {status.type && (
              <div className={`form-status ${status.type}`} data-testid="form-status">
                {status.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{status.message}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="footer-new" data-testid="footer">
    <div className="section-container">
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="footer-logo">INTONA 👑</div>
          <p className="footer-tagline">
            Stories for Little Crowns.<br/>
            Storywear for Little Readers.
          </p>
          <p className="footer-sub">
            Caribbean heritage · Natural hair pride<br/>
            Potwa & English · Ages 0–8
          </p>
        </div>
        <div>
          <p className="footer-col-head">Explore</p>
          <ul className="footer-links">
            <li><Link to="/">Home</Link></li>
            <li><a href="/#storywear">Storywear</a></li>
            <li><a href="/#how-it-works">How It Works</a></li>
            <li><a href="/#christmas">Christmas Drop</a></li>
            <li><Link to="/archive">Original Stories Archive</Link></li>
          </ul>
        </div>
        <div>
          <p className="footer-col-head">Connect</p>
          <ul className="footer-links">
            <li><a href={ETSY_URL} target="_blank" rel="noopener noreferrer">Etsy Shop</a></li>
            <li><a href="https://ourhairitage.com" target="_blank" rel="noopener noreferrer">Our Hairitage</a></li>
            <li><a href="mailto:theresahatchett5@gmail.com">Email Us</a></li>
            <li><a href="/#contact">Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom-new">
        <p>© 2026 Intona Stories. Created by Theresa Hatchett. All rights reserved.</p>
        <p className="footer-ip">All content, characters, and intellectual property are protected under copyright law.</p>
        <p className="footer-affirmation-new">Cheve ou bèl. Your hair is beautiful. 👑</p>
      </div>
    </div>
  </footer>
);

// ─────── ARCHIVE PAGE (Original INTONA Content) ───────

const charactersData = [
  { emoji: "👶🏿", role: "Protagonist", name: "INTONA", desc: "Plump, joyful, dramatic, wise. Big natural 4C hair in two round puffs. Speaks in third person — \"Intona wants to know!\" She is curious about everything and afraid of nothing.", potwa: "Ti cheri · Ti pitit mwen" },
  { emoji: "👩🏿", role: "Mama", name: "MAMA", desc: "Tall, graceful, fierce. Bright African headwrap, flowing Ankara print dress. Warm but never soft when it matters. She teaches with patience and responds to hair shaming with calm, unshakeable dignity.", potwa: "Manman · Ti cheri" },
  { emoji: "👴🏿", role: "Granpapa", name: "GRANPAPA", desc: "Elder, storyteller, historian. White hair, kind deep-set eyes, a simple dashiki, a carved wooden chair. The voice of the ancestors. Gentle humour. Deep certainty. He always knows.", potwa: "Granpapa · Granmoun" }
];

const episodesData = [
  { num: "01", type: "heritage", typeLabel: "Heritage", title: "Intona Wakes Up Curious", desc: "The very first question — Intona wants to know where she comes from.", affirmation: "\"Kote ou soti? — Where do you come from?\"", age: "Ages 0–8", videoUrl: null },
  { num: "07", type: "hair", typeLabel: "Hair Pride", title: "My Hair Is Not Wrong", desc: "Someone at nursery says Intona's hair is \"too big like a bush.\" Mama and Granpapa show her the truth — her hair is a crown.", affirmation: "\"Cheve mwen se kouròn mwen — My hair is my crown\"", age: "Ages 3–6", videoUrl: null },
  { num: "08", type: "hair", typeLabel: "Hair Pride", title: "Cornrows Are a Map to Freedom", desc: "Granpapa reveals that cornrow patterns were used as escape maps during slavery.", affirmation: "\"Tres mwen pote istwa — My braids carry history\"", age: "Ages 5–8", videoUrl: null },
  { num: "13", type: "hair", typeLabel: "Hair Pride", title: "Nobody Touches Intona's Hair Without Asking", desc: "A stranger reaches out to touch Intona's puffs without permission.", affirmation: "\"Cheve mwen pa yon jwèt — My hair is not a toy\"", age: "Ages 3–6", videoUrl: null },
  { num: "19", type: "hair", typeLabel: "Hair Pride", title: "Your Hair Is Dressed for the Wedding", desc: "At a family wedding, someone tells Intona her natural hair is \"too untidy.\"", affirmation: "\"Cheve ou abiye pou fèt la — Your hair is dressed for the celebration\"", age: "Ages 3–6", videoUrl: null },
  { num: "20", type: "hair", typeLabel: "Hair Pride", title: "New School. New Friend. Same Crown.", desc: "Intona's first day at new school. A boy says her puffs look like \"a bush.\" Then she meets Zara.", affirmation: "\"Cheve mwen se mwen — My hair is me\"", age: "Ages 3–6", videoUrl: "https://customer-assets.emergentagent.com/job_site-maker-645/artifacts/ihbcoper_0313%20%281%29%282%29.mp4", potwaWords: ["Zanmi", "Cheve mwen se mwen", "Fyète", "Natirèl", "Kouròn"] }
];

const potwaWordsData = [
  { word: "Kouròn", meaning: "Crown" }, { word: "Cheve", meaning: "Hair" },
  { word: "Bèl", meaning: "Beautiful" }, { word: "Fanmi", meaning: "Family" },
  { word: "Fyète", meaning: "Pride" }, { word: "Zanmi", meaning: "Friend" },
  { word: "Istwa", meaning: "History / Story" }, { word: "Libète", meaning: "Freedom" },
  { word: "Zansèt", meaning: "Ancestors" }, { word: "Eritaj", meaning: "Heritage" },
  { word: "Natirèl", meaning: "Natural" }, { word: "Tanbou", meaning: "Drum" }
];

const heritageFactsData = [
  { emoji: "🗺️", title: "Cornrows as Escape Maps", body: "During slavery, braid patterns encoded safe routes. Seeds were hidden inside braids. Hair was resistance." },
  { emoji: "🐚", title: "Cowrie Shells", body: "Ancient African currency worn in hair for wealth, fertility, and spiritual protection across generations." },
  { emoji: "✊🏿", title: "The Afro", body: "Symbol of Black liberation in the 1960s–70s. A direct rejection of Eurocentric beauty standards." },
  { emoji: "👑", title: "Crown Revival Project", body: "Teaching the next generation to love, understand, and celebrate their natural hair." }
];

const VideoModal = ({ episode, onClose }) => {
  if (!episode) return null;
  return (
    <div className="video-modal-overlay" onClick={onClose} data-testid="video-modal">
      <div className="video-modal" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal-close" onClick={onClose} data-testid="modal-close-btn">
          <X size={24} />
        </button>
        <div className="video-modal-header">
          <span className={`ep-type ${episode.type}`}>{episode.typeLabel}</span>
          <h2 className="video-modal-title">Episode {episode.num}: {episode.title}</h2>
        </div>
        {episode.videoUrl ? (
          <div className="video-container">
            <video controls autoPlay className="episode-video" data-testid="episode-video">
              <source src={episode.videoUrl} type="video/mp4" />
            </video>
          </div>
        ) : (
          <div className="video-placeholder">
            <Play size={64} />
            <p>Video coming soon!</p>
          </div>
        )}
        <div className="video-modal-body">
          <p className="ep-desc">{episode.desc}</p>
          <p className="ep-affirmation">{episode.affirmation}</p>
          {episode.potwaWords && (
            <div className="episode-potwa-words">
              <h4>Potwa Words in this Episode:</h4>
              <div className="potwa-tags">
                {episode.potwaWords.map((word, idx) => (
                  <span key={idx} className="potwa-tag">{word}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ArchivePage = () => {
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  return (
    <div className="archive-page" data-testid="archive-page">
      <Navigation />
      
      <section className="archive-hero">
        <div className="section-container">
          <p className="section-eyebrow light">The Archive</p>
          <h1 className="archive-title">Original INTONA Stories</h1>
          <p className="archive-subtitle">
            The characters, episodes, heritage research, and Potwa language 
            that make up the INTONA story world.
          </p>
        </div>
      </section>

      <section className="archive-section" style={{ background: 'var(--cream)' }}>
        <div className="section-container">
          <p className="section-eyebrow">Who is Intona?</p>
          <h2 className="section-heading dark">A baby girl with a <em>crown</em> that never comes off</h2>
          <div className="archive-about-grid">
            <div>
              <p className="body-text dark">
                Intona is a plump, joyful baby girl with big natural 4C hair in two round puffs. 
                Curious, dramatic, wise beyond her years — she speaks in third person and sees the 
                world with wonder.
              </p>
              <p className="body-text dark">
                Alongside Mama and Granpapa, Intona explores Caribbean and African heritage, natural 
                hair history, and what it means to carry your ancestors inside you.
              </p>
              <blockquote className="about-quote">
                "A queen does not ask permission to wear her crown."<br/>— Granpapa
              </blockquote>
            </div>
            <div className="archive-stats">
              <div className="stat-item"><strong>20</strong><span>Episodes</span></div>
              <div className="stat-item"><strong>40+</strong><span>Potwa Words</span></div>
              <div className="stat-item"><strong>0–8</strong><span>Years</span></div>
              <div className="stat-item"><strong>3</strong><span>Characters</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="archive-section dark-bg">
        <div className="section-container">
          <p className="section-eyebrow light">The Family</p>
          <h2 className="section-heading light">Three voices. <em>One lineage.</em></h2>
          <div className="archive-characters">
            {charactersData.map((char, i) => (
              <div key={i} className="archive-char-card" data-testid={`archive-char-${char.name.toLowerCase()}`}>
                <span className="char-emoji">{char.emoji}</span>
                <p className="char-role">{char.role}</p>
                <h3 className="char-name">{char.name}</h3>
                <p className="char-desc">{char.desc}</p>
                <p className="char-potwa">{char.potwa}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="archive-section" style={{ background: '#120D06' }}>
        <div className="section-container">
          <p className="section-eyebrow light">The Series</p>
          <h2 className="section-heading light">Twenty episodes. <em>One truth.</em></h2>
          <div className="archive-episodes">
            {episodesData.map((ep, i) => (
              <div 
                key={i} 
                className={`archive-ep-card ${ep.videoUrl ? 'has-video' : ''}`}
                onClick={() => setSelectedEpisode(ep)}
                data-testid={`archive-ep-${ep.num}`}
              >
                {ep.videoUrl && <div className="featured-badge">VIDEO</div>}
                <div className="ep-card-header">
                  <div className="ep-num">{ep.num}</div>
                  <div className="ep-meta">
                    <p className={`ep-type ${ep.type}`}>{ep.typeLabel}</p>
                    <h3 className="ep-title">{ep.title}</h3>
                  </div>
                </div>
                <div className="ep-card-body">
                  <p className="ep-desc">{ep.desc}</p>
                  <p className="ep-affirmation">{ep.affirmation}</p>
                </div>
                <div className="ep-card-footer">
                  <span className="ep-age">{ep.age}</span>
                  <span className="ep-watch">
                    {ep.videoUrl ? <><Play size={14} /> Watch</> : 'Coming Soon'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="archive-section" style={{ background: 'var(--red)' }}>
        <div className="section-container">
          <p className="section-eyebrow light">The Research</p>
          <h2 className="section-heading light">Our Hairitage — The Truth About Black Hair</h2>
          <div className="archive-heritage-grid">
            <div className="heritage-intro">
              <p className="body-text light">
                Every Intona episode draws on real history from the Our Hairitage documentary 
                by filmmaker Adanna Madueke.
              </p>
              <a href="https://ourhairitage.com" target="_blank" rel="noopener noreferrer" className="btn-outline-light">
                Visit ourhairitage.com <ChevronRight size={16} />
              </a>
            </div>
            <div className="heritage-facts-list">
              {heritageFactsData.map((fact, i) => (
                <div key={i} className="fact-item">
                  <span className="fact-emoji">{fact.emoji}</span>
                  <p className="fact-title">{fact.title}</p>
                  <p className="fact-body">{fact.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="archive-section" style={{ background: 'var(--brown)' }}>
        <div className="section-container">
          <p className="section-eyebrow light">The Language</p>
          <h2 className="section-heading light">Learn <em>Potwa</em> with Intona</h2>
          <div className="archive-potwa-grid">
            {potwaWordsData.map((item, i) => (
              <div key={i} className="archive-potwa-card">
                <p className="potwa-word">{item.word}</p>
                <p className="potwa-meaning">{item.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      
      {selectedEpisode && (
        <VideoModal episode={selectedEpisode} onClose={() => setSelectedEpisode(null)} />
      )}
    </div>
  );
};

// Admin Page Component
const AdminPage = () => {
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('messages');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();

  const ADMIN_PASSWORD = 'TheresaH12&?';

  useEffect(() => {
    const auth = sessionStorage.getItem('intona_admin_auth');
    if (auth === 'true') setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
    else setLoading(false);
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('intona_admin_auth', 'true');
      setAuthError('');
    } else {
      setAuthError('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('intona_admin_auth');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [messagesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/orders`)
      ]);
      setMessages(messagesRes.data);
      setOrders(ordersRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page" data-testid="admin-page">
        <nav className="admin-nav">
          <Link to="/" className="nav-logo">INTONA <span>👑</span></Link>
          <h1 className="admin-title">Message Center</h1>
          <button onClick={() => navigate('/')} className="btn-outline-light admin-back">
            ← Back to Site
          </button>
        </nav>
        <div className="admin-login-container">
          <div className="admin-login-box">
            <div className="admin-login-icon">🔐</div>
            <h2 className="admin-login-title">Admin Access</h2>
            <p className="admin-login-subtitle">Enter password to view messages</p>
            <form onSubmit={handleLogin} className="admin-login-form">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password" className="admin-password-input" data-testid="admin-password-input" />
              <button type="submit" className="btn-primary" data-testid="admin-login-btn">
                Access Messages
              </button>
              {authError && <p className="admin-auth-error">{authError}</p>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-page">
      <nav className="admin-nav">
        <Link to="/" className="nav-logo">INTONA <span>👑</span></Link>
        <h1 className="admin-title">Admin Dashboard</h1>
        <div className="admin-nav-actions">
          <button onClick={handleLogout} className="btn-outline-light admin-logout">Logout</button>
          <button onClick={() => navigate('/')} className="btn-outline-light admin-back">← Back to Site</button>
        </div>
      </nav>
      <div className="admin-content">
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="tab-messages"
          >
            <Mail size={16} /> Messages ({messages.length})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
            data-testid="tab-orders"
          >
            <ShoppingBag size={16} /> Storywear Orders ({orders.length})
          </button>
          <button onClick={fetchData} className="btn-primary refresh-btn">Refresh</button>
        </div>

        {loading ? (
          <div className="admin-loading"><div className="loading-spinner"></div><p>Loading...</p></div>
        ) : error ? (
          <div className="admin-error"><AlertCircle size={48} /><p>{error}</p>
            <button onClick={fetchData} className="btn-primary">Try Again</button></div>
        ) : activeTab === 'messages' ? (
          messages.length === 0 ? (
            <div className="admin-empty"><Mail size={64} /><h3>No messages yet</h3>
              <p>When visitors submit the contact form, their messages will appear here.</p></div>
          ) : (
            <div className="messages-list">
              {messages.map((msg, index) => (
                <div key={msg.id || index} className="message-card">
                  <div className="message-header">
                    <div className="message-sender"><User size={18} /><span className="sender-name">{msg.name}</span></div>
                    <div className="message-time"><Clock size={14} /><span>{formatDate(msg.timestamp)}</span></div>
                  </div>
                  <div className="message-email"><Mail size={14} /><a href={`mailto:${msg.email}`}>{msg.email}</a></div>
                  <div className="message-body"><p>{msg.message}</p></div>
                  <div className="message-actions">
                    <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${msg.email}&su=Re: Your message to INTONA`}
                      target="_blank" rel="noopener noreferrer" className="reply-btn">
                      Reply via Gmail
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          orders.length === 0 ? (
            <div className="admin-empty"><ShoppingBag size={64} /><h3>No orders yet</h3>
              <p>When families submit Storywear inquiries, they will appear here.</p></div>
          ) : (
            <div className="messages-list">
              {orders.map((order, index) => (
                <div key={order.id || index} className="message-card order-card" data-testid={`order-${index}`}>
                  <div className="message-header">
                    <div className="message-sender"><User size={18} /><span className="sender-name">{order.parent_name}</span></div>
                    <div className="message-time"><Clock size={14} /><span>{formatDate(order.timestamp)}</span></div>
                  </div>
                  <div className="message-email"><Mail size={14} /><a href={`mailto:${order.email}`}>{order.email}</a></div>
                  <div className="order-details">
                    <div className="order-detail-row">
                      <span className="order-label">Book:</span>
                      <span className="order-value">{order.book_title}{order.book_author ? ` by ${order.book_author}` : ''}</span>
                    </div>
                    <div className="order-detail-row">
                      <span className="order-label">Theme:</span>
                      <span className="order-value">{order.story_theme}</span>
                    </div>
                    <div className="order-detail-row">
                      <span className="order-label">Child Age:</span>
                      <span className="order-value">{order.child_age}</span>
                    </div>
                    <div className="order-detail-row">
                      <span className="order-label">Pyjama Size:</span>
                      <span className="order-value">{order.pyjama_size}</span>
                    </div>
                    {order.preferences && (
                      <div className="order-detail-row">
                        <span className="order-label">Preferences:</span>
                        <span className="order-value">{order.preferences}</span>
                      </div>
                    )}
                  </div>
                  <div className="message-actions">
                    <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${order.email}&su=Your Storywear Inquiry - ${order.book_title}`}
                      target="_blank" rel="noopener noreferrer" className="reply-btn">
                      Reply via Gmail
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const openOrder = () => setOrderFormOpen(true);
  
  return (
    <>
      <Navigation />
      <Hero onOpenOrderForm={openOrder} />
      <StorywearIntro onOpenOrderForm={openOrder} />
      <HowItWorks />
      <StorywearPromise />
      <ChristmasGallery />
      <ChristmasDrop onOpenOrderForm={openOrder} />
      <ContactForm />
      <Footer />
      <OrderFormModal isOpen={orderFormOpen} onClose={() => setOrderFormOpen(false)} />
    </>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="app-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
